import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { naiveWallClock } from '../utils/naive-timestamp.utils';

/**
 * Що означає настінний час у колонці, з якої приїхало значення.
 *
 * Колонок `timestamp without time zone` у базі 133 зі 133, і зона в них не зберігається — тож
 * прочитати їх однаково не можна: те саме `2026-08-01 11:30:00.000` означає різні речі залежно від
 * того, хто його записав.
 *
 * - `utc-instant` (за замовчуванням) — колонку пише сервер через `now()` на UTC-сесії. Настінний
 *   час у ній UTC, тож його треба перевести в київський. Це майже всі позначки часу застосунку:
 *   `created_at`, `updated_at`, `confirmed_at`, `completed_at`, `status_changed_at` і решта.
 * - `operator-date` — оператор обрав у календарі **лише дату** (`revaluation_date`, прибуткова
 *   `invoice_date`). Показуємо записану дату як є.
 * - `operator-datetime` — оператор обрав дату **й час** (`occurred_at`, видаткова `invoice_date`).
 *   Показуємо записаний настінний час як є.
 *
 * У цих двох переводити нікуди не треба — і не можна. Настінний час там уже київський: це буквально
 * те, що оператор бачив у календарі. Перерахунок додав би йому 3 години, а на date-only полі рядок
 * після 21:00 поїхав би ще й наступною добою.
 *
 * - `utc-date` — **перехідний режим, рівно на одну колонку:** `return_date` у повернень покупця.
 *   Дата-тільки, але декод UTC, бо ця колонка не містить настінних значень взагалі. Писав її лише
 *   `buyer-return-create` через `toISOString()`, тобто там або зсунуті рядки
 *   (`2026-07-31 21:00:00.000` на обране 1 серпня), або чиста дата вже після правки — і UTC-декод
 *   правильний для обох, тоді як `operator-date` показав би старі повернення днем раніше.
 *   Прибрати, коли бек змігрує колонку: зсунуті рядки розпізнаються точно (рівно `21:00:00.000`
 *   влітку / `22:00:00.000` взимку, нуль мілісекунд — інших форм `toISOString()` локальної півночі
 *   не дає). Тоді це стане `operator-date`. Умова, за якої режим стає хибним: якщо в колонку
 *   почне писати імпорт із реальним часом — тоді рядок після 21:00 поїде наступною добою.
 */
export type UkrDateTimeKind = 'utc-instant' | 'utc-date' | 'operator-date' | 'operator-datetime';

const ZONE_SUFFIX = /[zZ]$|[+-]\d{2}:?\d{2}$/;

const pad = (value: number) => `${value}`.padStart(2, '0');

@Component({
    selector: 'app-ukr-date-time',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    templateUrl: './ukr-date-time.component.html',
    styles: [
        `
      :host {
        display: inline-block;
      }
    `,
    ],
})
export class UkrDateTimeComponent {
    dateTime = input<string | Date | null | undefined>();

    /** Див. `UkrDateTimeKind`. За замовчуванням — серверна колонка, тобто поведінка не змінилась. */
    kind = input<UkrDateTimeKind>('utc-instant');

    private static dateTimeFormatter = new Intl.DateTimeFormat('uk-UA', {
        timeZone: 'Europe/Kyiv',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    private static dateFormatter = new Intl.DateTimeFormat('uk-UA', {
        timeZone: 'Europe/Kyiv',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    formattedDate = computed(() => {
        const value = this.dateTime();
        if (!value) {
            return '-';
        }

        const kind = this.kind();
        const dateOnly = kind === 'operator-date' || kind === 'utc-date';
        const wallClock = kind === 'operator-date' || kind === 'operator-datetime';

        try {
            if (typeof value === 'string') {
                const raw = value.trim();

                // Значення без зони на полі оператора — настінний київський час. Складаємо рядок з
                // компонентів, повз `Date` і `Intl`: локалізувати тут нічого (формат той самий
                // `DD.MM.YYYY HH:mm`), а будь-який `Date` посередині втягнув би зону, якої в
                // значенні немає.
                if (wallClock && !ZONE_SUFFIX.test(raw)) {
                    const wall = naiveWallClock(raw);
                    if (!wall) {
                        return '-';
                    }

                    const day = `${pad(wall.day)}.${pad(wall.month)}.${wall.year}`;
                    if (dateOnly) {
                        return day;
                    }
                    return `${day} ${pad(wall.hour ?? 0)}:${pad(wall.minute ?? 0)}`;
                }

                return this.formatMoment(this.parseMoment(raw), dateOnly);
            }

            return this.formatMoment(value instanceof Date ? value : new Date(value), dateOnly);
        } catch (e) {
            console.error('Error formatting date:', e);
            return '-';
        }
    });

    /**
     * Рядок → момент. Значення без зони читається як UTC — так його записав `now()`. Значення, що
     * несе зону, лишається як є: це вже момент, і на полі оператора теж (див. `formattedDate`).
     */
    private parseMoment(raw: string): Date {
        if (ZONE_SUFFIX.test(raw)) {
            return new Date(raw);
        }
        const iso = raw.replace(' ', 'T');
        return new Date(iso.includes('T') ? `${iso}Z` : iso);
    }

    private formatMoment(date: Date, dateOnly: boolean): string {
        if (isNaN(date.getTime())) {
            return '-';
        }

        const formatter = dateOnly
            ? UkrDateTimeComponent.dateFormatter
            : UkrDateTimeComponent.dateTimeFormatter;

        return formatter.format(date).replace(',', '');
    }
}
